// CustomTabs.js
import React, { useEffect, useState } from "react";
import { Box, Tab } from "@mui/material";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { colors, radius } from "../../../../utils";
import {
  TAB_ACTIVE_BG,
  TAB_ACTIVE_TEXT,
  TAB_INACTIVE_BG,
  TAB_INACTIVE_TEXT,
} from "./tabPalette";

// gordon-0915-block-layout-tab-disabled-state-explainer: a disabled tab
// (e.g. every block's "Layout" tab before it has content) used to render
// pixel-identical to a normal inactive tab and swallow clicks silently.
export const DISABLED_TAB_EXPLAINER =
  "Add content first - choose or upload media to unlock Layout";

const CustomTabs = ({ selectedTab, handleTabChange, options, style }) => {
  // Which disabled tab's explainer is currently shown, if any.
  const [explainerKey, setExplainerKey] = useState(null);

  // If the open tab's `disabled` flag flips false (content was just added),
  // drop the stale explainer instead of leaving it stuck open under a tab
  // that now works normally.
  useEffect(() => {
    if (explainerKey !== null && !options.get(explainerKey)?.disabled) {
      setExplainerKey(null);
    }
  }, [options, explainerKey]);

  const tabStyle = (value, disabled) => ({
    // WCAG-AA on the tab strip's own fills (see tabPalette.js). The previous
    // pair measured 3.30:1 active / 1.78:1 inactive; the inactive one was low
    // enough that QA read a working tab as a disabled one.
    color: selectedTab === value ? TAB_ACTIVE_TEXT : TAB_INACTIVE_TEXT,
    fontSize: "14px",
    lineHeight: "20px",
    fontWeight: "400",
    // Match the app's rounded-selection radius (radius.md 8px) so the purple
    // tab highlight is consistent with the segment-settings / card highlights.
    borderRadius: `${radius.md}px`,
    textTransform: "capitalize",
    borderTop: `2px solid`,
    borderColor:
      selectedTab === value ? `${colors.PrimaryPurple600}` : colors.background,
    backgroundColor: selectedTab === value ? TAB_ACTIVE_BG : TAB_INACTIVE_BG,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "0px",
    minWidth: 0,
    ...(disabled ? { opacity: 0.5, cursor: "not-allowed" } : {}),
  });

  // A disabled tab must never activate its panel — the native MUI `disabled`
  // prop would achieve that, but it also blocks the click event entirely
  // (browsers/RTL don't fire click on disabled buttons), which would make it
  // impossible to show the explainer. So switching is blocked here instead,
  // at the TabList level, leaving the Tab itself clickable.
  const onTabListChange = (event, newValue) => {
    if (options.get(newValue)?.disabled) {
      return;
    }
    setExplainerKey(null);
    handleTabChange(event, newValue);
  };

  const onTabClick = (key, disabled) => () => {
    if (!disabled) {
      return;
    }
    setExplainerKey((prev) => (prev === key ? null : key));
  };

  return (
    <TabContext value={selectedTab}>
      <Box>
        <TabList
          sx={{
            display: "flex",
            justifyContent: "space-between",
            height: "48px",
            marginBottom: "20px",
            "& .MuiTabs-indicator": {
              display: "none !important",
            },
          }}
          onChange={onTabListChange}
          variant="fullWidth"
        >
          {Array.from(options.keys()).map((key, index) => {
            const disabled = Boolean(options.get(key).disabled);
            return (
              <Tab
                key={key}
                label={options.get(key).label}
                title={disabled ? undefined : options.get(key).tooltip}
                value={key}
                aria-disabled={disabled}
                onClick={onTabClick(key, disabled)}
                sx={{
                  marginLeft: index !== 0 ? "10px" : 0,
                  transition: "background-color 0.2s ease",
                  "&:hover": disabled
                    ? undefined
                    : { backgroundColor: "#EFE9FE !important" },
                }}
                style={{ ...tabStyle(key, disabled), ...style }}
              />
            );
          })}
        </TabList>
      </Box>
      {/* Inline explainer for whichever disabled tab was just clicked/tapped —
          role="status" + aria-live="polite" so screen readers announce it
          without the assertive interruption of an alert. Absent entirely
          when no disabled tab has been clicked, so it never takes up layout
          space in the common (nothing disabled) case. */}
      {explainerKey !== null && (
        <Box
          role="status"
          aria-live="polite"
          sx={{
            fontSize: "13px",
            lineHeight: "18px",
            color: colors.textGrey,
            padding: "0 4px 12px",
            marginTop: "-12px",
          }}
        >
          {DISABLED_TAB_EXPLAINER}
        </Box>
      )}
      {Array.from(options.keys()).map((key) => (
        <TabPanel style={{ padding: 0 }} key={key} value={key}>
          {options.get(key)?.component}
        </TabPanel>
      ))}
    </TabContext>
  );
};

export default CustomTabs;
